import {
  CONSENT_GIVEN_EVENT_NAME,
  getUserConsent,
} from "@puregym/cookieconsent";
import { useEffect, useRef, useState } from "react";

export const COOKIE_CONSENT_INITIALISED_EVENT_NAME = "cookieConsentInitialised";

export type CookieConsentType = "functional" | "statistic" | "marketing";

const useCookieConsentInitilisation = () => {
  const [hasInitialised, setHasInitialised] = useState<boolean>(false);
  const isEventListenerAdded = useRef<boolean>(false);

  useEffect(() => {
    if (typeof window === "undefined" || isEventListenerAdded.current) {
      return;
    }

    const handleCookieChange = (_event: CustomEvent) => {
      setHasInitialised(true);
    };

    window.addEventListener(
      COOKIE_CONSENT_INITIALISED_EVENT_NAME,
      handleCookieChange as EventListener
    );

    isEventListenerAdded.current = true;

    return () => {
      window.removeEventListener(
        COOKIE_CONSENT_INITIALISED_EVENT_NAME,
        handleCookieChange as EventListener
      );
    };
  }, []);

  return hasInitialised;
};

export const useCookieConsent = (consentType: CookieConsentType) => {
  const [cookieValue, setCookieValue] = useState<boolean>(false);
  const isEventListenerAdded = useRef<boolean>(false);
  const hasInitialised = useCookieConsentInitilisation();

  if (
    process.env.GATSBY_AUTO_ACCEPT_AB_TASTY_COOKIES?.toLowerCase() === "true"
  ) {
    return true;
  }

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }

    const initialCookieValue = getUserConsent(consentType) ?? false;
    setCookieValue(initialCookieValue);
  }, [consentType]);

  useEffect(() => {
    if (
      typeof window === "undefined" ||
      !hasInitialised ||
      isEventListenerAdded.current
    ) {
      return;
    }

    const handleCookieChange = (_event: CustomEvent) => {
      const newCookieValue = getUserConsent(consentType) ?? false;
      setCookieValue(newCookieValue);
    };

    window.addEventListener(
      CONSENT_GIVEN_EVENT_NAME,
      handleCookieChange as EventListener
    );

    isEventListenerAdded.current = true;

    return () => {
      window.removeEventListener(
        CONSENT_GIVEN_EVENT_NAME,
        handleCookieChange as EventListener
      );
    };
  }, [hasInitialised, isEventListenerAdded, consentType]);

  return cookieValue;
};

export default useCookieConsent;
